import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    Flex,
    HStack,
    Img,
    Text,
} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import PriceCard from '../price-card'
import {ChevronRightIcon} from '../icons'
import {getDamBaseUrl} from '../../custom-hooks/use-dam-image'
import {useProducts} from '@salesforce/commerce-sdk-react'
import ProductVariantsSelectorCard from './product-variants-selector-card'

const ProductVariantsSelector = ({
    styles,
    isOpen,
    onClose,
    product,
    activeCurrency,
    lastItemThreshold,
    priceData,
    LastFewItemsVariant,
    InfoStockIcon,
    OutOfStockIcon,
    onOpen,
}) => {
    const variations = product?.c_enabledVariationGroups || []
    const current = variations.filter((el) => product?.c_subModelIdentifier === el?.productId)
    const others = variations.filter((el) => product?.c_subModelIdentifier !== el?.productId)
    const orderedVariations = [...current, ...others]

    const isOutOfStockProduct = !product?.inventory?.orderable && !product?.c_isPDPWhereToBuy
    const hasMoreVariants = product?.c_enabledVariationGroups?.length > 1

    const baseUrl = getDamBaseUrl()

    const {data} = useProducts({
        parameters: {
            ids: product?.c_enabledVariationGroups
                ?.filter((group) => group.productId !== product.c_subModelIdentifier)
                .map((group) => group.productId),
            expand: ['availability', 'images'],
        },
        headers: {
            'x-debug': 'product-variants-index',
        },
    })

    return (
        <Box>
            <Drawer isOpen={isOpen} placement="right" onClose={onClose} size={'full'}>
                <DrawerOverlay />
                <DrawerContent {...styles?.drawerContentContainer}>
                    <DrawerCloseButton {...styles?.drawerCloseButton} />
                    <DrawerHeader {...styles?.drawerHeaderContainer}>
                        <Text>
                            <FormattedMessage
                                id="variants.label.availableOptions"
                                defaultMessage="Available options ({options})"
                                values={{
                                    options: product?.c_enabledVariationGroups?.length || 1,
                                }}
                            />
                        </Text>
                    </DrawerHeader>

                    <DrawerBody {...styles?.drawerBodyContainer}>
                        {orderedVariations.map((el, i) => {
                            return (
                                <ProductVariantsSelectorCard
                                    key={i}
                                    data={data}
                                    el={el}
                                    styles={styles}
                                    onClose={onClose}
                                    product={product}
                                    activeCurrency={activeCurrency}
                                    lastItemThreshold={lastItemThreshold}
                                    LastFewItemsVariant={LastFewItemsVariant}
                                    InfoStockIcon={InfoStockIcon}
                                    OutOfStockIcon={OutOfStockIcon}
                                />
                            )
                        })}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
            <Button {...styles?.currentVariantContainer} onClick={hasMoreVariants ? onOpen : null}>
                <Flex {...styles?.currentVariantInfoContainer}>
                    <PriceCard
                        priceData={priceData}
                        product={product}
                        activeCurrency={activeCurrency}
                        variant={'pdpSelectorCurrentVariant'}
                        productDetailCurrentPriceTextStyle="body-small"
                        productDetailListPriceTextStyle="body-small"
                    />
                    <Flex {...styles?.currentVariantInfo}>
                        <Text {...styles?.currentVariantTitle}>
                            {product?.c_pdpSelectorName || product?.name}
                        </Text>
                        {product?.c_pdpSelectorImage && (
                            <Box>
                                <Img
                                    alt={product?.id}
                                    src={`${baseUrl}/${product.c_pdpSelectorImage}`}
                                    {...styles?.variantsImage}
                                />
                            </Box>
                        )}
                    </Flex>
                </Flex>

                {hasMoreVariants && (
                    <Flex {...styles?.rightIconButton}>
                        <ChevronRightIcon {...styles?.rightIcon} />
                    </Flex>
                )}
            </Button>
            {hasMoreVariants &&
                (isOutOfStockProduct ? (
                    <Flex {...styles?.outOfStockInfoContainer}>
                        <Text>
                            <FormattedMessage
                                defaultMessage="Sorry, this model is out of stock. Good news, though - {count} other options are available."
                                id="variants.label.outOfStockSelectorInfo"
                                values={{
                                    count: (product?.c_enabledVariationGroups?.length || 1) - 1,
                                }}
                            />
                        </Text>
                        <Button
                            variant={'text-button'}
                            onClick={onOpen}
                            {...styles?.outOfStockSeeAllButton}
                        >
                            <HStack>
                                <Text>
                                    <FormattedMessage
                                        defaultMessage="Check them out here"
                                        id="variants.label.outOfStockSelectorSeeAll"
                                    />
                                </Text>
                            </HStack>
                        </Button>
                    </Flex>
                ) : (
                    <Flex>
                        <Button variant={'text-button'} onClick={onOpen} {...styles?.seeAllButton}>
                            <HStack>
                                <Text>
                                    <FormattedMessage
                                        defaultMessage="+ Other {count} options"
                                        id="variants.label.seeAll"
                                        values={{
                                            count:
                                                (product?.c_enabledVariationGroups?.length || 1) -
                                                1,
                                        }}
                                    />
                                </Text>
                            </HStack>
                        </Button>
                    </Flex>
                ))}
        </Box>
    )
}

ProductVariantsSelector.propTypes = {
    styles: PropTypes.object,
    isOpen: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    product: PropTypes.object,
    activeCurrency: PropTypes.string,
    priceData: PropTypes.object,
    lastItemThreshold: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    InfoStockIcon: PropTypes.elementType,
    OutOfStockIcon: PropTypes.elementType,
    LastFewItemsVariant: PropTypes.string,
    data: PropTypes.object,
}

export default ProductVariantsSelector
